import { createAsyncThunk, createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  addPromotionDiscountApi,
  editPromotionDiscountApi,
  getDiscountSettingsApi,
  getPromotionDiscountApi,
  getPromotionSettingsApi,
  searchDiscountProductApi,
  updatePromotionDiscountApi,
} from "../../../services/discount";

export const getAllPromotionDiscounts = createAsyncThunk("promotionDiscount/getPromotionList", async (filters, { dispatch }) => {
    const response = await getPromotionDiscountApi(filters);
    dispatch(setTableRowCount(response.response.count));
    dispatch(setActiveList(response.response));
    return response.response.data;
});

export const getDiscountSettings = createAsyncThunk("discount/settingsDiscount", async (data, { dispatch }) => {
  const response = await getDiscountSettingsApi();
  return response.response;
});

// SETTINGS
export const getPromotionSettings = createAsyncThunk("promotionDiscount/settingsPromotionDiscount", async (data, { dispatch }) => {
  const response = await getPromotionSettingsApi();
  return response.response;
});

// EDIT PROMOTION DISCOUNT
export const editPromotionDiscount = createAsyncThunk("promotionDiscount/editPromotionDiscount", async (id, { dispatch }) => {
  try {
    const response = await editPromotionDiscountApi(id);
    return response.response;
  } catch (e) {
    toast.error("Failed to load promotion discount details");
    return Promise.reject(e);
  }
});

// UPDATE PROMOTION DISCOUNT
export const updatePromotionDiscount = createAsyncThunk("promotionDiscount/updatePromotionDiscount", async (data, { dispatch }) => {
    const response = await updatePromotionDiscountApi(data, data.id);

    if (response.status === false) {
      toast.error(response.message || "Failed to update Promotion Discount");
      return response;
    }

    toast.success("Promotion Discount Updated Successfully");
    return response;
  
});

// PRODUCT SEARCH
export const productDiscountSearch = createAsyncThunk('discount/productSearch', async data => {
	const response = await searchDiscountProductApi(data);
	return response.response;
});

// CREATE PROMOTION DISCOUNT
export const storePromotionDiscount = createAsyncThunk("promotionDiscount/addNewPromotionDiscount", async (data, { dispatch }) => {
  try {
    const response = await addPromotionDiscountApi(data);
    toast.success("Promotion Discount Added Successfully");
    return response.response;
  } catch (e) {
    toast.error(e.message || `Failed to update promotion discount`);
    return Promise.reject(e);
  }
});

const discountAdapter = createEntityAdapter({});

export const { selectAll: selectDiscounts, selectById: selectDiscountById } = discountAdapter.getSelectors((state) => state.discount.discount);

const discountSlice = createSlice({
  name: "discount",
  initialState: discountAdapter.getInitialState({
    tableRowCount: 0,
    settings: {
      users:[],
      discountType: [],
      status: [],
      countries : [],
      combinedDiscounts : []
    },
    discountSettings: {
      discount_type: [],
      status: [],
      users: [],
      product_discount: [],
      product_discount_count: 0,
      order_discount: [],
      order_discount_count: 0,
      shipping_discount: [],
      shipping_discount_count: 0,
    },
    activeListing: {
      isActiveDiscount: false,
      activeDiscountCount: 0,
    },
    promotionDiscountData: { data: [], pagination: { total: 1 } },
    modal_data: [],
    discountDialogs: {
      type: 1,
      title: "",
      props: {
        isOpen: false,
      },
    },
    addCountryDialog: {
      component: "Countries",
      type: "new",
      props: {
        open: false,
      },
      data: null,
    },
    productsDialog: {
      component: "Products",
      type: "new",
      props: {
        open: false,
      },
      data: null,
    },
    productsDiscountDialog: {
      component: "Products",
      type: "new",
      props: {
        open: false,
      },
      data: null,
    },
  }),
  reducers: {
    setPromotionDiscountSearchText: {
      reducer: (state, action) => {
        state.searchText = action.payload;
      },
      prepare: (event) => ({ payload: event.target.value || "" }),
    },
    setTableRowCount: (state, action) => {
      state.tableRowCount = action.payload;
    },
    setSettings: (state, action) => {
      state.settings.users = action.payload.user;
      state.settings.status = action.payload.status;
      state.settings.discountType = action.payload.type;
      state.settings.combinedDiscounts = action.payload.combined_discounts;
      state.settings.countries = action.payload.countries;
    },
    setActiveList: (state, action) => {
      state.activeListing.isActiveDiscount = action.payload.is_shopify_active_discount;
      state.activeListing.activeDiscountCount = action.payload.shopify_active_discount;
    },
    setDiscountSettings: (state, action) => {
      state.discountSettings.discount_type = action.payload.type;
      state.discountSettings.status = action.payload.status;
      state.discountSettings.users = action.payload.user;
      state.discountSettings.product_discount = action.payload.combined_discounts.product_discount.data;
      state.discountSettings.product_discount_count = action.payload.combined_discounts.product_discount.count;
      state.discountSettings.order_discount = action.payload.combined_discounts.order_discount.data;
      state.discountSettings.order_discount_count = action.payload.combined_discounts.order_discount.count;
      state.discountSettings.shipping_discount = action.payload.combined_discounts.shipping_discount.data;
      state.discountSettings.shipping_discount_count = action.payload.combined_discounts.shipping_discount.count;
    },
    setPromotionDiscountData: (state, action) => {
      state.promotionDiscountData = action.payload;
    },
    setModalData: (state, action) => {
      state.modal_data = action.payload;
    },
    openDiscountDialogs: (state, action) => {
      state.discountDialogs = action.payload;
    },
    closeDiscountDialog: (state, action) => {
      state.discountDialogs = {
        type: 1,
        title: "",
        props: {
          isOpen: false,
        },
      };
    },
    openCountryDialog: (state, action) => {
      state.addCountryDialog = {
        component: "Look Book",
        type: "new",
        props: {
          isOpen: true,
        },
        data: null,
      };
    },
    closeCountryDialog: (state, action) => {
      state.addCountryDialog = {
        type: "new",
        props: {
          isOpen: false,
        },
        data: null,
      };
    },
    openProductsDialog: (state, action) => {
      state.productsDialog = {
        component: "Look Book",
        type: "new",
        props: {
          isOpen: true,
        },
        data: null,
      };
    },
    closeProductsDialog: (state, action) => {
      state.productsDialog = {
        type: "new",
        props: {
          isOpen: false,
        },
        data: null,
      };
    },
  },
  extraReducers: {
    [getAllPromotionDiscounts.fulfilled]: discountAdapter.setAll,
    // [retrieveData.fulfilled]: dashboardAdapter.setAll,
    // [updateStaff.fulfilled]: staffsAdapter.setAll
    // [deleteStaff.fulfilled]: staffsAdapter.removeOne,
  },
});

export const {
  setTableRowCount,
  setSettings,
  setDiscountSettings,
  setPromotionDiscountData,
  setModalData,
  setActiveList,
  openDiscountDialogs,
  closeDiscountDialog,
  closeCountryDialog,
  openCountryDialog,
  openProductsDialog,
  closeProductsDialog
} = discountSlice.actions;

export default discountSlice.reducer;
